import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { generateReceiptHeader, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

export const generateInstallmentSettingPDF = async (studentData, installments, collegeInfo = null) => {
  console.log('Generating PDF with student data:', studentData);
  console.log('Seat Booking Amount:', studentData.seat_booking_amount);
  console.log('Admission Fee:', studentData.admission_fee);
  
  const doc = new jsPDF();

  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  // Get college info - prefer parameter over localStorage
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateInstallmentSettingPDF: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  let yPos = await generateReceiptHeader(doc, collegeInfo, 'INSTALLMENT SETTING REPORT');

  // Date
  doc.setFontSize(9);
  doc.text(`Generated: ${formatDate(new Date())}`, 14, yPos);
  yPos += 10;

  // Student Information
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Student Information', 14, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  
  const studentInfo = [
    ['Student Name', studentData.full_name || 'N/A'],
    ['Admission Number', studentData.admission_number || 'N/A'],
    ['Admission Date', studentData.admission_date ? formatDate(studentData.admission_date) : 'N/A'],
    ['Course', studentData.course_name || 'N/A'],
    ['Session', studentData.session || 'N/A'],
    ['Semester', studentData.semester || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: studentInfo,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Fee Details
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Fee Details', 14, yPos);
  
  yPos += 8;
  
  const seatBooking = studentData.seat_booking_amount || studentData.booking_amount || 0;
  const admissionFee = studentData.admission_fee || 0;
  const totalCourseFee = studentData.total_course_fee || 0;
  
  const feeDetails = [
    ['Total Course Fee', `${CURRENCY} ${formatINR(totalCourseFee)}`],
    ['Seat Booking Amount', `${CURRENCY} ${formatINR(seatBooking)}`],
    ['Admission Fee', `${CURRENCY} ${formatINR(admissionFee)}`]
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: feeDetails,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal', fontStyle: 'bold', textColor: [41, 128, 185] }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Installment Payment Option
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Installment Payment Option', 14, yPos);
  
  yPos += 8;
  
  // Determine if semester wise or custom
  const installmentMode = installments.length > 0 && installments[0].semester_number !== undefined 
    ? 'Semester Wise' 
    : 'Custom Date Wise';
  
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Payment Mode: ${installmentMode}`, 14, yPos);
  
  yPos += 10;

  // Installments Table
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Installment Details', 14, yPos);
  
  yPos += 2;

  const installmentTableData = installments.map(inst => {
    const instNumber = inst.semester_number 
      ? `Semester ${inst.semester_number}` 
      : `Installment ${inst.installment_number || 'N/A'}`;
    
    const paymentDate = inst.due_date 
      ? formatDate(inst.due_date)
      : 'N/A';
    
    const lateFine = inst.fine_setting_name || 'No Fine';
    
    return [
      instNumber,
      `${CURRENCY} ${formatINR((inst.amount || 0))}`,
      paymentDate,
      lateFine
    ];
  });

  doc.autoTable({
    startY: yPos,
    head: [['Semester/Installment', 'Payment Amount', 'Payment Date', 'Late Fine']],
    body: installmentTableData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 45 },
      3: { cellWidth: 50 }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Summary
  const totalInstallmentAmount = installments.reduce((sum, inst) => sum + (inst.amount || 0), 0);
  
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Summary', 14, yPos);
  
  yPos += 8;
  
  const summaryData = [
    ['Total Installments', `${installments.length}`],
    ['Total Installment Amount', `${CURRENCY} ${formatINR(totalInstallmentAmount)}`]
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: summaryData,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 100, fontStyle: 'bold' },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold', textColor: [41, 128, 185] }
    }
  });

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'italic');
  doc.text('This is a computer-generated document.', 105, pageHeight - 20, { align: 'center' });
  doc.text('Fee Management System', 105, pageHeight - 15, { align: 'center' });

  // Save the PDF
  doc.save(`Installment_Setting_${studentData.admission_number || 'Report'}.pdf`);

  return doc;
};
